import { Component, Inject } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { AdminAdmDefinitionService } from "../../api/services/admin-adm-definition.service";
import { AdmTable } from "../../api/models/adm-table";
import { AdmTableColumn } from "../../api/models/adm-table-column";
import { AdmRelationship } from "../../api/models/adm-relationship";
import { AdmRerouteRequest } from "../../api/models/adm-reroute-request";
import { AdmRerouteResponse } from "../../api/models/adm-reroute-response";
import { DialogResult, DialogResultType } from "../DialogResult";

export interface AdmRerouteLineageDialogData {
  parentTableId: number;
  tables: AdmTable[];
  columns: AdmTableColumn[];
  relationships: AdmRelationship[];
}

const pathIds = (path: string | undefined): number[] => (path || "").split(",").filter((x) => x).map((x) => Number(x));

// Admin ADM Definition > Flattened Columns > Re-route lineage. Moves a stretch of the lineage (relationship path)
// of several flattened columns of one table at once, for example inserting a status table between postings and
// companies. Only the flattened columns' paths change - relationships are never created or edited here - and
// ADMs are regenerated separately and manually (Regenerate affected ADMs on the Flattened Columns tab).
@Component({
  selector: "adm-reroute-lineage-dialog",
  templateUrl: "./adm-reroute-lineage-dialog.component.html",
})
export class AdmRerouteLineageDialogComponent {
  public Result: DialogResult = { ResultType: DialogResultType.Cancel, ResultData: null };

  // Step 1: the lineage to take the segment from, and the segment (a contiguous range of its hops).
  public referenceColumnId: number | null = null;
  public fromHop = 0;
  public throughHop = 0;

  // Step 2: the replacement, built one existing relationship at a time.
  public newSegment: number[] = [];

  // Step 3: which columns to move. Off (the default): only the columns the admin ticks, starting with those whose
  // lineage is exactly the one picked in step 1. On: every flattened column of the table whose lineage passes
  // through the replaced hops, including ones with a different tail or extra hops.
  public applyToAll = false;
  public picked = new Set<number>();

  public preview: AdmRerouteResponse | null = null;
  public working = false;
  public error = "";

  constructor(
    public dialogRef: MatDialogRef<AdmRerouteLineageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdmRerouteLineageDialogData,
    private definitionService: AdminAdmDefinitionService
  ) {
    this.lineageGroups = this.buildLineageGroups();
  }

  // The flattened columns of the parent table (retired ones included: their path is still real).
  get flattened(): AdmTableColumn[] {
    return this.data.columns.filter((c) => c.columnKind === 1 && c.admTableId === this.data.parentTableId);
  }

  tableName(id: number): string {
    const t = this.data.tables.find((x) => x.admTableId === id);
    return t ? t.tableName : `#${id}`;
  }

  private relationship(id: number): AdmRelationship | undefined {
    return this.data.relationships.find((r) => r.admRelationshipId === id);
  }

  private tableOfColumn(columnId: number): number | null {
    return this.data.columns.find((c) => c.admTableColumnId === columnId)?.admTableId ?? null;
  }

  // "from_table > to_table" for one hop.
  hopLabel(id: number): string {
    const r = this.relationship(id);
    if (!r) return `#${id}`;
    const from = this.tableOfColumn(r.fromColumnId);
    const to = this.tableOfColumn(r.toColumnId);
    return `${from === null ? "?" : this.tableName(from)} > ${to === null ? "?" : this.tableName(to)}`;
  }

  // "postings > indeed_companies > universal_companies" for a whole path.
  lineageLabel(path: string | undefined): string {
    const ids = pathIds(path);
    if (ids.length === 0) return "";
    const first = this.relationship(ids[0]);
    const start = first ? this.tableOfColumn(first.fromColumnId) : null;
    const tables = ids.map((id) => {
      const r = this.relationship(id);
      const to = r ? this.tableOfColumn(r.toColumnId) : null;
      return to === null ? `#${id}` : this.tableName(to);
    });
    return [start === null ? "?" : this.tableName(start), ...tables].join(" > ");
  }

  // One entry per distinct lineage on the table, so the admin picks "the industries lineage" rather than a column.
  // Built once: a getter returning fresh objects each change-detection pass would make the *ngFor recreate every
  // mat-option continuously and hang the dialog.
  public lineageGroups: { column: AdmTableColumn; count: number }[] = [];

  private buildLineageGroups(): { column: AdmTableColumn; count: number }[] {
    const groups = new Map<string, { column: AdmTableColumn; count: number }>();
    for (const c of this.flattened) {
      const key = c.flattenRelationshipPath || "";
      const g = groups.get(key);
      if (g) g.count++;
      else groups.set(key, { column: c, count: 1 });
    }
    return Array.from(groups.values());
  }

  trackGroup(_: number, g: { column: AdmTableColumn }): number {
    return g.column.admTableColumnId;
  }

  get referenceHops(): number[] {
    const c = this.flattened.find((x) => x.admTableColumnId === this.referenceColumnId);
    return c ? pathIds(c.flattenRelationshipPath) : [];
  }

  onReferenceChange(columnId: number) {
    this.referenceColumnId = columnId;
    this.fromHop = 0;
    this.throughHop = 0;
    this.newSegment = [];
    this.resetPicked();
    this.clearPreview();
  }

  onSegmentChange() {
    if (this.throughHop < this.fromHop) this.throughHop = this.fromHop;
    this.newSegment = [];
    this.resetPicked();
    this.clearPreview();
  }

  // The default selection: the columns whose lineage is exactly the reference lineage.
  private resetPicked() {
    const reference = this.flattened.find((x) => x.admTableColumnId === this.referenceColumnId);
    this.picked = new Set(this.flattened.filter((c) => !!reference && c.flattenRelationshipPath === reference.flattenRelationshipPath).map((c) => c.admTableColumnId));
  }

  onApplyToAllChange(checked: boolean) {
    this.applyToAll = checked;
    this.clearPreview();
  }

  get oldSegment(): number[] {
    return this.referenceHops.slice(this.fromHop, this.throughHop + 1);
  }

  // The columns whose lineage contains the segment as a contiguous run - the only ones that can move with it.
  get candidates(): AdmTableColumn[] {
    const seg = this.oldSegment;
    if (seg.length === 0) return [];
    return this.flattened.filter((c) => {
      const p = pathIds(c.flattenRelationshipPath);
      for (let i = 0; i + seg.length <= p.length; i++) if (seg.every((id, k) => p[i + k] === id)) return true;
      return false;
    });
  }

  get selectedColumnIds(): number[] {
    return this.candidates.filter((c) => this.applyToAll || this.picked.has(c.admTableColumnId)).map((c) => c.admTableColumnId);
  }

  toggleColumn(columnId: number, checked: boolean) {
    if (checked) this.picked.add(columnId);
    else this.picked.delete(columnId);
    this.clearPreview();
  }

  // The new segment starts where the old one started and every step starts where the previous one ended.
  get nextRelationships(): AdmRelationship[] {
    const seg = this.oldSegment;
    if (seg.length === 0) return [];
    const first = this.relationship(seg[0]);
    let current = first ? this.tableOfColumn(first.fromColumnId) : null;
    for (const id of this.newSegment) {
      const r = this.relationship(id);
      current = r ? this.tableOfColumn(r.toColumnId) : null;
    }
    return this.data.relationships.filter((r) => !r.retired && r.cardinality !== 2 && this.tableOfColumn(r.fromColumnId) === current);
  }

  addStep(relationshipId: number) {
    this.newSegment = [...this.newSegment, relationshipId];
    this.clearPreview();
  }

  removeLastStep() {
    this.newSegment = this.newSegment.slice(0, -1);
    this.clearPreview();
  }

  clearPreview() {
    this.preview = null;
    this.error = "";
  }

  private get request(): AdmRerouteRequest {
    return { parentTableId: this.data.parentTableId, columnIds: this.selectedColumnIds, oldSegment: this.oldSegment, newSegment: this.newSegment };
  }

  get canPreview(): boolean {
    return !this.working && this.selectedColumnIds.length > 0 && this.oldSegment.length > 0 && this.newSegment.length > 0;
  }

  get hasErrors(): boolean {
    return (this.preview?.issues || []).some((i) => i.severity === 1);
  }

  get canApply(): boolean {
    return !this.working && !!this.preview && !this.hasErrors && (this.preview.changes || []).length > 0;
  }

  private errorText(e: any): string {
    return (e && (typeof e.error === "string" ? e.error : e.message)) || "The request failed";
  }

  onPreview() {
    this.working = true;
    this.error = "";
    this.definitionService.adminAdmDefinitionPreviewReroute(this.request).subscribe({
      next: (r) => { this.working = false; this.preview = r; },
      error: (e) => { this.working = false; this.preview = null; this.error = this.errorText(e); },
    });
  }

  onApply() {
    const message = `Move the lineage of ${this.preview?.changes?.length} flattened column(s)? No ADM is regenerated until you choose to.`;
    if (!confirm(message)) return;
    this.working = true;
    this.error = "";
    this.definitionService.adminAdmDefinitionApplyReroute(this.request).subscribe({
      next: (r) => {
        this.working = false;
        if (!r.applied) { this.preview = r; return; }
        this.Result.ResultType = DialogResultType.Ok;
        this.Result.ResultData = r;
        this.dialogRef.close(this.Result);
      },
      error: (e) => { this.working = false; this.error = this.errorText(e); },
    });
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  static showDialog(dialog: MatDialog, data: AdmRerouteLineageDialogData): Observable<DialogResult> {
    return dialog.open(AdmRerouteLineageDialogComponent, { disableClose: false, width: "900px", data }).afterClosed();
  }
}
